import { useCallback, useEffect, useRef, useState } from 'react';

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type VoiceWindow = Window & { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };

export function useKrysVoice(onTranscript: (text: string) => void) {
  const transcript = useRef(onTranscript);
  transcript.current = onTranscript;
  const recognition = useRef<Recognition | null>(null);
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const utterance = useRef<SpeechSynthesisUtterance | null>(null);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const speechWindow = window as VoiceWindow;
  const canListen = Boolean(speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition);
  const canSpeak = 'speechSynthesis' in window;

  const stopSpeaking = useCallback(() => {
    if (utterance.current) {
      utterance.current.onend = null;
      utterance.current.onerror = null;
      window.speechSynthesis?.cancel();
      utterance.current = null;
    }
    setSpeaking(false);
  }, []);

  const stopListening = useCallback(() => {
    if (finishTimer.current) clearTimeout(finishTimer.current);
    finishTimer.current = null;
    const current = recognition.current;
    recognition.current = null;
    if (current) {
      current.onresult = null; current.onend = null; current.onerror = null;
      current.abort();
    }
    setListening(false);
  }, []);

  // Stop recording gracefully so the browser can deliver its final transcript.
  // Abort is reserved for reset, power-off and unmount, where late results are unwanted.
  const finishListening = useCallback(() => {
    const current = recognition.current;
    if (!current) return;
    if (finishTimer.current) clearTimeout(finishTimer.current);
    finishTimer.current = setTimeout(() => {
      if (recognition.current === current) stopListening();
    }, 2500);
    try { current.stop(); } catch { stopListening(); }
  }, [stopListening]);

  const startListening = useCallback((language: string) => {
    stopSpeaking();
    stopListening();
    setVoiceError('');
    const voiceWindow = window as VoiceWindow;
    const Constructor = voiceWindow.SpeechRecognition || voiceWindow.webkitSpeechRecognition;
    if (!Constructor) { setVoiceError('Voice input is unavailable in this browser. You can type below.'); return; }
    const current = new Constructor();
    recognition.current = current;
    current.lang = language;
    current.continuous = false;
    current.interimResults = true;
    let receivedText = false;
    let failed = false;
    current.onresult = event => {
      if (recognition.current !== current) return;
      const text = Array.from(event.results).map(result => result[0]?.transcript || '').join(' ').trim().slice(0, 2000);
      if (text) { receivedText = true; transcript.current(text); }
    };
    current.onerror = event => {
      if (recognition.current !== current || event.error === 'aborted') return;
      failed = true;
      const errors: Record<string, string> = {
        'not-allowed': 'Microphone access was not granted. Allow it in your browser or type instead.',
        'service-not-allowed': 'This browser cannot use speech recognition. Try Chrome or type instead.',
        network: 'The browser speech recognition service could not connect. Try Chrome or type instead.',
        'audio-capture': 'No microphone is available. Check your microphone settings.',
        'no-speech': 'No speech was recognized. Check the EN / 中文 input language and try again.',
      };
      setVoiceError(errors[event.error] || 'Speech recognition failed. Try again or type below.');
    };
    current.onend = () => {
      if (recognition.current === current) {
        if (finishTimer.current) clearTimeout(finishTimer.current);
        finishTimer.current = null;
        recognition.current = null; setListening(false);
        if (!receivedText && !failed) setVoiceError('No text was received. Check the EN / 中文 input language. If this keeps happening, try Chrome.');
      }
    };
    try { current.start(); setListening(true); }
    catch { stopListening(); setVoiceError('Voice input could not start. You can type below.'); }
  }, [stopListening, stopSpeaking]);

  const speak = useCallback((text: string) => {
    stopSpeaking();
    stopListening();
    if (!('speechSynthesis' in window)) return;
    const current = new SpeechSynthesisUtterance(text);
    current.lang = /[\u3400-\u9fff]/.test(text) ? 'zh-CN' : 'en-US';
    current.rate = 1;
    const voice = window.speechSynthesis.getVoices().find(item => item.lang === current.lang);
    if (voice) current.voice = voice;
    current.onend = () => { if (utterance.current === current) { utterance.current = null; setSpeaking(false); } };
    current.onerror = () => {
      if (utterance.current === current) {
        utterance.current = null; setSpeaking(false);
        setVoiceError('Audio could not play. The written reply is still available.');
      }
    };
    utterance.current = current;
    setSpeaking(true);
    window.speechSynthesis.speak(current);
  }, [stopListening, stopSpeaking]);

  useEffect(() => {
    const stopWhenHidden = () => { if (document.hidden) { stopListening(); stopSpeaking(); } };
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      document.removeEventListener('visibilitychange', stopWhenHidden);
      stopListening(); stopSpeaking();
    };
  }, [stopListening, stopSpeaking]);

  return { listening, speaking, voiceError, canListen, canSpeak, startListening, finishListening, stopListening, stopSpeaking, speak };
}
